import { ProgramTypes } from 'legacy/lut/classes/models/ProgramTypes'
import { IProgramType } from 'legacy/lut/features/partner360/configuration'

import { getAvailableProgramTypes } from 'partner360/helpers/offerings/getAvailableProgramTypes'
import { getOfferingsInfo } from 'partner360/helpers/offerings/getOfferingsInfo'

import type { ICockpitCompletenessData, ICockpitPartnerOfferingsCatalog } from '../model/types'

import { getAcknowledgedOfferingData } from './getAcknowledgedOfferingData'
import { getOfferingOrderMap } from './getOfferingOrderMap'

export interface IManagerOfferingCounts {
  offering: ProgramTypes
  acknowledged: number
  total: number
  /** The partners that have the offering enabled: the ones `total` counts. */
  partnerIds: string[]
  /** The partners of `partnerIds` that signed the offering: the ones `acknowledged` counts. */
  acknowledgedPartnerIds: string[]
}

const isPartnerOfferingAcknowledged = (
  partnerId: string,
  offering: ProgramTypes,
  completenessData: ICockpitCompletenessData[]
) => Boolean(getAcknowledgedOfferingData(completenessData, partnerId, offering))

const getEnabledOfferings = ({ offerings }: ICockpitPartnerOfferingsCatalog): IProgramType[] =>
  getOfferingsInfo(getAvailableProgramTypes(offerings)).enabledOfferings

export const getManagerOfferingsInfo = (
  offeringsCatalog: ICockpitPartnerOfferingsCatalog[],
  completenessData: ICockpitCompletenessData[]
): IManagerOfferingCounts[] => {
  const partnerEnabledOfferings = offeringsCatalog.map((partner) => ({
    partnerId: partner.partnerId,
    enabledOfferings: getEnabledOfferings(partner),
  }))

  const orderByOffering = getOfferingOrderMap(offeringsCatalog)

  return Array.from(orderByOffering.keys())
    .sort((a, b) => orderByOffering.get(a)! - orderByOffering.get(b)!)
    .map((offering) => {
      const partnersWithOffering = partnerEnabledOfferings.filter(({ enabledOfferings }) =>
        enabledOfferings.some((it) => it.programType === offering)
      )
      const partnerIds = partnersWithOffering.map(({ partnerId }) => partnerId)
      const acknowledgedPartnerIds = partnerIds.filter((partnerId) =>
        isPartnerOfferingAcknowledged(partnerId, offering, completenessData)
      )

      return {
        offering,
        acknowledged: acknowledgedPartnerIds.length,
        total: partnerIds.length,
        partnerIds,
        acknowledgedPartnerIds,
      }
    })
}

export const getManagerOverallCounts = (offeringCounts: IManagerOfferingCounts[]) =>
  offeringCounts.reduce(
    (acc, { acknowledged, total }) => ({
      acknowledged: acc.acknowledged + acknowledged,
      total: acc.total + total,
    }),
    { acknowledged: 0, total: 0 }
  )
